import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MapLayerMouseEvent, MapRef } from 'react-map-gl';
import { ClickRoadSectionCommand } from '../../../core/mapV2/commands/ClickRoadSectionCommand';
import { CloseRightPanelCommand } from '../../../core/mapV2/commands/CloseRightPanelCommand';
import { lineBounds } from '../../../core/mapV2/geo';
import { focusRequestQuery, listHoveredIdQuery, perimeterQuery, selectedIdsQuery } from '../../../core/mapV2/queries';
import { modifierOf } from '../../../core/mapV2/selection';
import { selectedRoadSectionsQuery } from '../../../core/reference/queries';
import { useAppDispatch, useAppSelector } from '../../../types';
import { ROAD_SECTIONS_LAYER_ID, ROAD_SECTIONS_SOURCE_ID } from './sources';

const featureRef = (id: string) => ({ source: ROAD_SECTIONS_SOURCE_ID, sourceLayer: ROAD_SECTIONS_SOURCE_ID, id });

const featureIdOf = (event: MapLayerMouseEvent): string | null => {
    const feature = event.features?.[0];
    const id = feature?.id ?? feature?.properties?.id;

    return id === undefined || id === null ? null : String(id);
};

export interface SectionContextMenuTarget {
    id: string;
    x: number;
    y: number;
}

// Hover, click, right-click and selection of referential sections on the map.
// Highlighting uses feature-state (`hover`, `selected`) on the section tiles, so nothing is redrawn.
// Returns the props to give to the Mapbox map, and the pending right-click menu.
export const useReferenceInteractions = (mapRef: RefObject<MapRef | null>, loaded: boolean) => {
    const dispatch = useAppDispatch();
    const selectedIds = useAppSelector(selectedIdsQuery);
    const selectedSections = useAppSelector(selectedRoadSectionsQuery);
    const focusRequest = useAppSelector(focusRequestQuery);
    const perimeter = useAppSelector(perimeterQuery);
    // Read by the pointer handlers, so they keep a stable identity while the filters change.
    const perimeterRef = useRef(perimeter);
    perimeterRef.current = perimeter;
    const listHoveredId = useAppSelector(listHoveredIdQuery);
    const [cursor, setCursor] = useState('');
    const [contextMenu, setContextMenu] = useState<SectionContextMenuTarget | null>(null);
    const hoveredId = useRef<string | null>(null);
    const appliedIds = useRef<Set<string>>(new Set());
    const listHovered = useRef<string | null>(null);
    // Read by the camera effect, which must run on a request only, not on every selection change.
    const selectedSectionsRef = useRef(selectedSections);
    selectedSectionsRef.current = selectedSections;

    const clearHover = useCallback(() => {
        const map = mapRef.current?.getMap();
        if (hoveredId.current !== null && map?.getSource(ROAD_SECTIONS_SOURCE_ID)) {
            map.setFeatureState(featureRef(hoveredId.current), { hover: false });
        }
        hoveredId.current = null;
        setCursor('');
    }, [mapRef]);

    const onMouseMove = useCallback(
        (event: MapLayerMouseEvent) => {
            const found = featureIdOf(event);
            // Sections the filters leave out are inert.
            const id = found !== null && perimeterRef.current.includes(found) ? found : null;
            if (id === hoveredId.current) {
                return;
            }
            clearHover();
            const map = mapRef.current?.getMap();
            if (id !== null && map?.getSource(ROAD_SECTIONS_SOURCE_ID)) {
                map.setFeatureState(featureRef(id), { hover: true });
                hoveredId.current = id;
                setCursor('pointer');
            }
        },
        [mapRef, clearHover],
    );

    const onClick = useCallback(
        (event: MapLayerMouseEvent) => {
            const found = featureIdOf(event);
            // A section the filters leave out counts as empty map.
            const id = found !== null && perimeterRef.current.includes(found) ? found : null;
            const modifier = modifierOf(event.originalEvent);
            setContextMenu(null);
            if (id !== null) {
                dispatch(ClickRoadSectionCommand({ id, modifier }));
            } else if (modifier === 'none') {
                // A click on empty map clears the selection; with a modifier it is most likely a missed
                // click on a thin line, so the selection is kept.
                dispatch(CloseRightPanelCommand());
            }
        },
        [dispatch],
    );

    const onContextMenu = useCallback((event: MapLayerMouseEvent) => {
        const id = featureIdOf(event);
        if (id === null || !perimeterRef.current.includes(id)) {
            return;
        }
        event.originalEvent.preventDefault();
        setContextMenu({ id, x: event.originalEvent.clientX, y: event.originalEvent.clientY });
    }, []);

    // Mirror the selection on the tiles. Feature-state is lost when the source is replaced (basemap
    // change), so it is applied again then.
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!loaded || !map) {
            return undefined;
        }
        let appliedSource: unknown = null;
        const apply = () => {
            const source = map.getSource(ROAD_SECTIONS_SOURCE_ID);
            if (!source) {
                return;
            }
            appliedSource = source;
            appliedIds.current.forEach((id) => {
                if (!selectedIds.includes(id)) {
                    map.setFeatureState(featureRef(id), { selected: false });
                }
            });
            selectedIds.forEach((id) => map.setFeatureState(featureRef(id), { selected: true }));
            appliedIds.current = new Set(selectedIds);
        };
        apply();
        // Only when the source was replaced: tiles loading while panning keep their feature-state.
        const onSourceData = (event: { sourceId?: string }) => {
            if (
                event.sourceId === ROAD_SECTIONS_SOURCE_ID &&
                map.getSource(ROAD_SECTIONS_SOURCE_ID) !== appliedSource
            ) {
                apply();
            }
        };
        map.on('sourcedata', onSourceData);

        return () => {
            map.off('sourcedata', onSourceData);
        };
    }, [mapRef, loaded, selectedIds]);

    // The list of retained sections highlights the section under the pointer on the map.
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!loaded || !map?.getSource(ROAD_SECTIONS_SOURCE_ID)) {
            return;
        }
        if (listHovered.current !== null) {
            map.setFeatureState(featureRef(listHovered.current), { listHover: false });
        }
        if (listHoveredId !== null) {
            map.setFeatureState(featureRef(listHoveredId), { listHover: true });
        }
        listHovered.current = listHoveredId;
    }, [mapRef, loaded, listHoveredId]);

    // The selection came from the panel (neighbor, extension) and may be off screen: bring it into view.
    useEffect(() => {
        if (focusRequest === 0) {
            return;
        }
        const bounds = lineBounds(selectedSectionsRef.current.flatMap((section) => section.line));
        if (bounds) {
            mapRef.current?.fitBounds(bounds, { padding: 120, maxZoom: 17 });
        }
    }, [mapRef, focusRequest]);

    return {
        interactiveLayerIds: loaded ? [ROAD_SECTIONS_LAYER_ID] : undefined,
        cursor,
        onMouseMove,
        onMouseLeave: clearHover,
        onClick,
        onContextMenu,
        contextMenu,
        closeContextMenu: () => setContextMenu(null),
    };
};
